/**
 * Radar's phone home screen, the rules as pure functions (docs/PLAN-2026-09-21-mobile-os-shell.md §3, the Radar table):
 * which apps stand where for this person, how a role reads. The components are the ported ones in ./mobile;
 * shell/phone.tsx feeds them with these.
 */
import type { RadarAppDefinition } from "../apps/registry";
import type { QuickAction, QuickActionContext } from "./mobile/types";

/** "3 h ago": coarse on purpose, a home screen line. */
export function ago(ms: number, now = Date.now()): string {
  const min = Math.max(0, Math.floor((now - ms) / 60_000));
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.floor(h / 24)} d ago`;
}

/** "editor" reads "Editor"; a role like "sat_reviewer" reads "Sat reviewer". */
export function roleLabel(role: string | undefined): string {
  if (!role) return "";
  const words = role.replace(/[_-]+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export interface PhoneAppsOptions {
  /** The app's name in the chosen language (Radar's useAppLabel). */
  label?: (id: string, fallback: string) => string;
  quickActions?: Partial<Record<string, (ctx: QuickActionContext) => QuickAction[]>>;
  badges?: Partial<Record<string, number>>;
}

/**
 * The apps this person may open, dressed for the phone: named in the chosen language, with the quick actions and
 * badges the assembly found.
 */
export function phoneApps(visible: RadarAppDefinition[], opts: PhoneAppsOptions): RadarAppDefinition[] {
  return visible.map((a) => {
    const def: RadarAppDefinition = { ...a };
    if (opts.label) {
      def.name = opts.label(a.id, a.name);
      // The short names are English; a name translated into another language keeps its full form.
      if (def.name !== a.name) delete def.short;
    }
    const qa = opts.quickActions?.[a.id];
    if (qa) def.quickActions = qa;
    const badge = opts.badges?.[a.id];
    if (badge !== undefined) def.badge = () => badge;
    return def;
  });
}
